
import { InterfaithResponse } from "../types";
import { 
  cleanSectionContent, 
  removeDuplicateParagraphs,
  deduplicateScriptureReferences,
  deduplicateObjections
} from "@/utils/responseFormatUtils";
import { supabase } from "@/integrations/supabase/client";

export async function fetchInterfaithResponse(faithGroup: string, question: string): Promise<{ response: InterfaithResponse }> {
  try {
    console.log(`Fetching interfaith response for ${faithGroup} about: "${question}"`);
    
    const { data, error } = await supabase.functions.invoke("interfaith-dialogue", {
      body: { 
        faith_group: faithGroup, 
        question 
      }
    });

    if (error) {
      console.error("Supabase function error:", error);
      throw new Error(`Failed to fetch response: ${error.message}`);
    }

    if (!data || !data.response) {
      console.error("Invalid response format:", data);
      throw new Error("Invalid response format received");
    }
    
    // Log raw response for debugging
    console.log("Raw API response:", data.response);

    // Clean section content by removing ### markers and other formatting issues
    const cleanMarkdownContent = (content: string): string => {
      if (!content) return '';
      // Remove section markers, markdown formatting, and trailing ###
      return content
        .replace(/###\s*/g, '')  // Remove ### markers
        .replace(/\*\*/g, '')    // Remove bold markers
        .replace(/\s*###\s*$/, '') // Remove trailing ###
        .trim();
    };

    // Create a cleaned version of the response with deduplication
    const cleanedResponse: InterfaithResponse = {
      directAnswer: data.response?.directAnswer ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.directAnswer))) : '',
      biblicalFoundation: data.response?.biblicalFoundation ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.biblicalFoundation))) : '',
      comparisonOfBeliefs: data.response?.comparisonOfBeliefs ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.comparisonOfBeliefs))) : '',
      refutationFromTheirTexts: data.response?.refutationFromTheirTexts ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.refutationFromTheirTexts))) : '',
      theologicalReasoning: data.response?.theologicalReasoning ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.theologicalReasoning))) : '',
      finalAppeal: data.response?.finalAppeal ? removeDuplicateParagraphs(cleanMarkdownContent(cleanSectionContent(data.response.finalAppeal))) : '',
      scriptureReferences: Array.isArray(data.response?.scriptureReferences) 
        ? deduplicateScriptureReferences(data.response.scriptureReferences)
            .map(ref => ({
              reference: ref.reference,
              text: ref.text,
              explanation: ref.explanation || `This verse addresses the question by showing the Christian perspective on ${ref.reference}.`
            }))
        : [],
      commonObjections: Array.isArray(data.response?.commonObjections) 
        ? deduplicateObjections(data.response.commonObjections) 
        : []
    };

    // Additional validation to remove section headers that might have been included in the content
    const possibleSectionHeaders = [
      'Direct Answer', 'Biblical Foundation', 'Comparison of Beliefs',
      'Refutation from Their Texts', 'Theological Reasoning', 'Final Appeal'
    ];
    
    // Check each text field for section headers that might indicate sections flowing together
    Object.keys(cleanedResponse).forEach(key => {
      if (typeof cleanedResponse[key] === 'string' && cleanedResponse[key]) {
        for (const header of possibleSectionHeaders) {
          if (cleanedResponse[key].includes(header + ':')) {
            console.warn(`Found section header "${header}:" in ${key} field, truncating content`);
            cleanedResponse[key] = cleanedResponse[key].split(header + ':')[0].trim();
          }
        }
      }
    });
    
    // Make sure scripture references have explanations
    if (cleanedResponse.scriptureReferences.length > 0) {
      cleanedResponse.scriptureReferences = cleanedResponse.scriptureReferences.map(ref => ({
        ...ref,
        explanation: ref.explanation || `This verse addresses the question by showing the Christian perspective on ${ref.reference}.`
      }));
    }
    
    console.log("Cleaned response:", {
      directAnswerLength: cleanedResponse.directAnswer?.length || 0,
      biblicalFoundationLength: cleanedResponse.biblicalFoundation?.length || 0,
      scriptureReferencesCount: cleanedResponse.scriptureReferences?.length || 0,
      comparisonOfBeliefsLength: cleanedResponse.comparisonOfBeliefs?.length || 0,
      refutationFromTheirTextsLength: cleanedResponse.refutationFromTheirTexts?.length || 0,
      theologicalReasoningLength: cleanedResponse.theologicalReasoning?.length || 0,
      commonObjectionsCount: cleanedResponse.commonObjections?.length || 0,
      finalAppealLength: cleanedResponse.finalAppeal?.length || 0
    });

    return { response: cleanedResponse };
  } catch (error) {
    console.error("Error fetching interfaith response:", error);
    throw error;
  }
}
